import {
  ContentTypes,
  ReferenceReadModel,
  ReferenceSourceType,
} from '@/client/services/api/graphql/gql/graphql';
import { HStack, Image, Stack, Text, useToken } from '@chakra-ui/react';

import { Dot } from '@/client/components/icons/individual';
import ExpertsColorChangeIcon from '@/client/components/admin/ai-agent/experts/ExpertsColorChangeIcon';
import format from 'date-fns-tz/format';
import { useGetHumanReadableContentType } from '@/client/services/hooks/content/useGetHumanReadableContentType';
import { useGetPublicImage } from '@/client/services/hooks/media/useGetPublicImage';
import { useNavigateByContentType } from '@/client/services/hooks/navigation/useNavigateByContentType';

interface ReferenceItemProps {
  reference: ReferenceReadModel;
  index: number;
  totalCount: number;
}

export default function ExchangeReferenceItem({ reference, index, totalCount }: ReferenceItemProps) {
  const [brandPrimary] = useToken('colors', ['brand.primary']);
  const { getPublicImage } = useGetPublicImage();
  const { getHumanReadableContentType } = useGetHumanReadableContentType();
  const { navigateByContentType } = useNavigateByContentType();

  const { contentType, contentId, contentTitle, image, createdAt, sourceType, link, color } =
    reference;

  const isInternalContentReference = sourceType === ReferenceSourceType.InternalContent;

  const isUrlReference = sourceType === ReferenceSourceType.Url;

  const isExpertReference = isInternalContentReference && contentType === ContentTypes.Expert;

  const title = isInternalContentReference || isExpertReference ? contentTitle : link;

  const isInvalidReferenceType =
    contentType === ContentTypes.Poll ||
    contentType === ContentTypes.MultiSessionWorkshopSession ||
    contentType === ContentTypes.Presentation ||
    contentType === ContentTypes.Quiz;

  const isInvalidInternalContentReference =
    isInternalContentReference && (!contentType || !contentId || !contentTitle);

  const isInvalidUrlReference = isUrlReference && !link;

  if (isInvalidReferenceType || isInvalidInternalContentReference || isInvalidUrlReference)
    return null;

  let usableImage = image;

  if (
    usableImage ===
      'https://d2277n3gvptnup.cloudfront.net/images/51d5f21b2f410a020b000002/43100c70-9d98-4b08-bb4f-8bdd70547f5f.jpeg' ||
    !usableImage
  ) {
    usableImage = getPublicImage('continu/app-images/card-default-logo.png');
  }

  const handleReferenceClick = () => {
    if (isInternalContentReference && contentType && contentId) {
      navigateByContentType(contentType, contentId);
    } else if (isUrlReference && link) {
      window.open(link, '_blank');
    } else {
      () => {};
    }
  };

  const isLast = index === totalCount - 1;

  return (
    <HStack
      spacing={3}
      cursor={isExpertReference ? 'default' : 'pointer'}
      alignItems="flex-start"
      onClick={isExpertReference ? undefined : handleReferenceClick}
      pb={isLast && isExpertReference ? 4 : undefined}
    >
      {!isExpertReference && (
        <Image
          src={usableImage}
          alt={(isInternalContentReference ? contentTitle : link) || 'Response reference image'}
          objectFit="cover"
          width="101px"
          minWidth="101px"
          height="56px"
          borderRadius="4px"
        />
      )}

      <Stack>
        <HStack spacing={1.5}>
          {isExpertReference && (
            <ExpertsColorChangeIcon color={color || brandPrimary} boxSize={6} />
          )}

          <Text
            as={isExpertReference ? 'i' : 'span'}
            fontSize="14px"
            lineHeight="18px"
            fontWeight={500}
            color="baseBlack"
            noOfLines={2}
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {title}
          </Text>
        </HStack>

        {!isExpertReference && isInternalContentReference && !!contentType && !!createdAt && (
          <HStack
            alignContent="center"
            alignItems="center"
            divider={<Dot boxSize={1.5} color="neutral.1300" />}
            fontSize="10px"
            fontWeight={500}
            color="neutral.1300"
          >
            <Text>{getHumanReadableContentType(contentType)}</Text>

            <Text>{format(new Date(createdAt), 'MMMM do, yyyy')}</Text>
          </HStack>
        )}
      </Stack>
    </HStack>
  );
}
